// 📁 src/lightversion/UpgradePopup.jsx

import React, { useEffect } from "react";
import {
  Box,
  Button,
  Typography,
  Card,
  CardContent,
  Grid,
  Dialog,
  Divider,
  Chip,
} from "@mui/material";
import { motion } from "framer-motion";
import { useNavigate } from "react-router-dom";

/* --------------------------------------------------
   🔐 CONSTANTS
-------------------------------------------------- */
const ENTRY_PAUSE_FLAG = "lite_resume_paused";
const POST_AUTH_INTENT = "post_auth_intent";

const UpgradePopup = ({ open = true }) => {
  const navigate = useNavigate();

  /* --------------------------------------------------
     🔒 FREEZE LITE AUTO-RESUME (CRITICAL)
  -------------------------------------------------- */
  useEffect(() => {
    localStorage.setItem(ENTRY_PAUSE_FLAG, "true");
  }, []);

  /* --------------------------------------------------
     🚀 PRO UPGRADE — REGISTER
  -------------------------------------------------- */
    const handleRegisterUpgrade = () => {
    // ✅ Track upgrade intent
    if (window.gtag) {
            window.gtag('event', 'upgrade_clicked', {
        source: 'lite_popup_register',
        step: localStorage.getItem("lite_resume_step") || "unknown",
      });
    }

    localStorage.setItem(POST_AUTH_INTENT, "pro-upgrade");
    localStorage.removeItem("lite_resume_step");
    navigate("/register?plan=pro");
  };

  /* --------------------------------------------------
     🔑 PRO UPGRADE — LOGIN
  -------------------------------------------------- */
    const handleLoginUpgrade = () => {
    // ✅ Track upgrade intent
    if (window.gtag) {
      window.gtag('event', 'upgrade_clicked', {
        source: 'lite_popup_login',
      });
    }

    localStorage.setItem(POST_AUTH_INTENT, "pro-upgrade");
    localStorage.removeItem("lite_resume_step");
    navigate("/login?intent=upgrade");
  };

  /* --------------------------------------------------
     🇮🇳 INDIA-RELEVANT PRO FEATURES
  -------------------------------------------------- */
  const proFeatures = [
    "Unlimited AI mentor conversations (no daily limits)",
    "Structured Market Validation (5P Framework)",
    "Idea → MVP → GTM guided execution path",
    "Business Model & Pricing Builder",
    "Pitch Deck & Storytelling Assistant",
    "Funding readiness & investor matching",
    "Save everything securely in one dashboard",
    "Founder learning paths (India-focused)",
  ];

  return (
    <Dialog open={open} maxWidth="md" fullWidth>
      <motion.div
        initial={{ opacity: 0, scale: 0.94 }}
        animate={{ opacity: 1, scale: 1 }}
        transition={{ duration: 0.25 }}
      >
        <Card sx={{ p: 3 }}>
          <CardContent>
            {/* ---------------- HEADER ---------------- */}
            <Typography
              variant="h4"
              fontWeight="bold"
              textAlign="center"
              sx={{ mb: 1 }}
            >
              You’ve done the hardest part
            </Typography>

            <Typography
              textAlign="center"
              color="text.secondary"
              sx={{ mb: 2 }}
            >
              Most people jump straight into ideas.  
              You took time to understand the problem.
            </Typography>

            <Box textAlign="center" sx={{ mb: 3 }}>
              <Chip
                label="Clarity achieved • Execution is next"
                color="primary"
                variant="outlined"
              />
            </Box>

            <Divider sx={{ my: 3 }} />

            {/* ---------------- VALUE FRAMING ---------------- */}
            <Typography
              variant="subtitle1"
              fontWeight="bold"
              textAlign="center"
              sx={{ mb: 2 }}
            >
              What Pro helps you do next
            </Typography>

            <Grid container spacing={2}>
              {proFeatures.map((feat) => (
                <Grid item xs={12} sm={6} key={feat}>
                  <Box
                    sx={{
                      display: "flex",
                      alignItems: "flex-start",
                      gap: 1,
                    }}
                  >
                    <span>✔</span>
                    <Typography variant="body2">{feat}</Typography>
                  </Box>
                </Grid>
              ))}
            </Grid>

            <Divider sx={{ my: 3 }} />

            {/* ---------------- INDIA-SAFE PRICING COPY ---------------- */}
            <Typography
              variant="body2"
              color="text.secondary"
              textAlign="center"
              sx={{ mb: 2 }}
            >
              Designed for students, first-time founders, and builders in India.  
              No lock-in. Cancel anytime.
            </Typography>

            {/* ---------------- PRIMARY CTA ---------------- */}
            <Box textAlign="center" sx={{ mt: 3 }}>
              <Button
                variant="contained"
                size="large"
                sx={{ px: 5, py: 1.5, fontSize: "1.05rem" }}
                onClick={handleRegisterUpgrade}
              >
                Unlock Pro & continue building →
              </Button>

              <Button
                variant="text"
                sx={{ mt: 1, display: "block" }}
                onClick={handleLoginUpgrade}
              >
                Already have an account? Log in →
              </Button>

              <Typography
                variant="caption"
                color="text.secondary"
                sx={{ display: "block", mt: 1 }}
              >
                Your Lite journey progress is محفوظ — nothing is lost.
              </Typography>
            </Box>

            {/* ---------------- EXIT (NO PRESSURE) ---------------- */}
            <Box textAlign="center" sx={{ mt: 3 }}>
              <Button
                onClick={() => navigate(-1)}
                sx={{ textTransform: "none" }}
              >
                Continue thinking in Lite for now
              </Button>
            </Box>
          </CardContent>
        </Card>
      </motion.div>
    </Dialog>
  );
};

export default UpgradePopup;
